import { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm, type UseFormRegister, type FieldErrors } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import {
  Input,
  Button,
  Divider,
  Checkbox,
  RadioGroup,
  RadioGroupItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { ArrowLeft, Check } from 'lucide-react';
import { AuthShell, PasswordInput } from './components/AuthShell';
import { authBrandPanel } from './components/authBrandPanel';
import { SsoNotice } from './components/SsoNotice';
import { AuthSwitchLink } from './components/AuthSwitchLink';
import { PasswordStrengthMeter, scorePassword } from './components/PasswordStrengthMeter';
import styles from './RegisterPage.module.css';
import form from './components/AuthForm.module.css';
import { TermsDialog } from './terms/TermsDialog';
import { COMPANY_NAME } from './terms/terms-content';
import { brand, brandCopyright } from '@/config/brand';
import { useAuth } from '@/auth/AuthProvider';
import { registerUser } from '@/api/auth-flow';
import { submitWebsiteLead } from '@/api/leads';
import { resolveAuthError } from '@/api/auth-error';
import { trackSignUp } from '@/lib/analytics-events';
import { GoogleSignInButton } from './components/GoogleSignInButton';
import { RegisterOtpStep } from './components/RegisterOtpStep';
import { returnPath, returnState } from './return-to';
import { BusinessLeadRegister } from './BusinessLeadRegister';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type AccountType = 'business' | 'individual';

// Plain subtitle under the heading (design renders this as muted body copy, not an
// info callout).
const signupSubtitle = brand.features.sso
  ? `Your account will be created using ${brand.ssoName} for unified access across all ${brand.shortName} services with enhanced security features.`
  : undefined;

const FOOTER = brandCopyright();

function TypeCard({
  value, selected, title, description, onSelect,
}: {
  value: AccountType;
  selected: boolean;
  title: string;
  description: string;
  onSelect: (v: AccountType) => void;
}) {
  return (
    <label
      onClick={() => onSelect(value)}
      className={`${styles.typeCard} ${selected ? styles.typeCardSelected : ''}`}
    >
      {/* Native radio kept for keyboard / screen-reader selection, visually replaced
          by the top-right mark below. */}
      <RadioGroupItem value={value} className={styles.srOnly} />
      <div className={styles.typeCardInner}>
        <div className={styles.typeCardTitle}>{title}</div>
        <div className={styles.typeCardDesc}>{description}</div>
        <span
          className={`${styles.indicator} ${selected ? styles.indicatorOn : ''}`}
          aria-hidden="true"
        >
          {selected ? <Check size={11} strokeWidth={3} /> : null}
        </span>
      </div>
    </label>
  );
}

interface FormValues {
  email: string;
  password: string;
  password_confirmation: string;
  contact_person: string;
  phone: string;
  company_name: string;
}

interface FieldHelpers {
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  watch: (name: keyof FormValues) => string;
}

const passwordRules = {
  required: 'Password is required.',
  maxLength: { value: 64, message: 'Password must be at most 64 characters.' },
  // Matches the design: at least 3 of {8+ chars, uppercase, number, symbol, 14+ chars}.
  validate: (v: string) =>
    scorePassword(v) >= 3 || 'Password does not meet the requirements above.',
};

function IndividualFields({ register, errors, watch }: FieldHelpers) {
  return (
    <>
      <Field label="Email" required className={form.field} error={errors.email?.message}>
        {(p) => (
          <Input {...p} type="email" autoComplete="email" invalid={!!errors.email}
            wrapperClassName={`${form.input} ${form.tall}`}
            {...register('email', {
              required: 'Email is required.',
              pattern: {
                value: EMAIL_RE,
                message: 'Enter a valid email address, e.g. you@company.com',
              },
            })} />
        )}
      </Field>
      <Field
        label="Password"
        required
        className={form.field}
        description="At least 8 characters, with one uppercase letter, one number, and one symbol."
        error={errors.password?.message}
      >
        {(p) => (
          <>
            <PasswordInput {...p} autoComplete="new-password" invalid={!!errors.password}
              wrapperClassName={`${form.input} ${form.tall}`}
              {...register('password', passwordRules)} />
            <PasswordStrengthMeter value={watch('password')} />
          </>
        )}
      </Field>
      <Field label="Password Confirmation" required className={form.field} error={errors.password_confirmation?.message}>
        {(p) => (
          <PasswordInput {...p} autoComplete="new-password" invalid={!!errors.password_confirmation}
            wrapperClassName={`${form.input} ${form.tall}`}
            {...register('password_confirmation', {
              required: 'Please confirm your password.',
              validate: (v) => v === watch('password') || 'Passwords do not match.',
            })} />
        )}
      </Field>
    </>
  );
}

/**
 * Register entry — the account-type chooser + Google. Business accounts go to the
 * B2B lead form (<BusinessLeadRegister>: DataCentral lead + free-email block);
 * individual accounts use the simple email/password form below.
 */
export default function RegisterPage() {
  return <StandardRegister />;
}

function StandardRegister() {
  const navigate = useNavigate();
  // The page that sent a visitor here to sign up (e.g. a team invitation link) — they land
  // back on it once the account exists, and keep it if they switch to Login instead.
  const next = returnPath(useLocation().state);
  const nextState = returnState(next);
  const { setSession } = useAuth();
  const [step, setStep] = useState<'type' | 'form' | 'otp'>('type');
  const [accountType, setAccountType] = useState<AccountType>('business');
  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      email: '', password: '', password_confirmation: '',
      contact_person: '', phone: '', company_name: '',
    },
  });

  const { mutateAsync } = useMutation({ mutationFn: registerUser });

  // The submitted individual form, held while the user verifies their email.
  const pendingRef = useRef<FormValues | null>(null);

  // Only the individual account type submits here — business registration is a
  // B2B lead form handled by <BusinessLeadRegister> (routed below). Instead of
  // creating the account immediately, hold the values and go to email
  // verification; the account is created only after the OTP is confirmed.
  const onFormSubmit = (values: FormValues) => {
    setError(null);
    pendingRef.current = values;
    setStep('otp');
  };

  // Create the account with the verified OTP. Returns an error message for the
  // OTP step to show, or null on success (then we navigate away).
  const completeRegister = async (otp: string): Promise<string | null> => {
    const values = pendingRef.current;
    if (!values) return 'Something went wrong. Please go back and try again.';
    try {
      const res = await mutateAsync({
        type: 'individual',
        email: values.email,
        password: values.password,
        password_confirmation: values.password_confirmation,
        otp,
      });
      if (res.errors?.length) {
        const m = res.errors[0].message ?? '';
        // Already-registered → send to sign in instead of a dead-end code form.
        if (/already.?exist/i.test(m)) {
          navigate('/auth/login', {
            state: { ...nextState, email: values.email, notice: 'You already have an account. Please sign in.' },
          });
          return null;
        }
        return m;
      }
      trackSignUp(accountType);
      // Capture a sales/marketing lead for the individual signup too (mirrors the
      // business flow). referrer + current link are attached inside submitWebsiteLead
      // from the landing attribution, identical to business. Best-effort: the account
      // is already created, so a lead failure must never block auto sign-in.
      void submitWebsiteLead({ email: values.email }).catch(() => {});
      // If the backend returns a session, sign in; otherwise send to login.
      // SSO registration returns Keycloak tokens — persist the real auth_method
      // so token refresh uses the right endpoint (matches InvitationPage).
      if (res.data?.access_token && setSession(res.data, res.data.auth_method ?? 'local')) {
        navigate(next, { replace: true });
      } else {
        navigate('/auth/login', { replace: true, state: nextState });
      }
      return null;
    } catch (e) {
      return resolveAuthError(e, 'Registration failed. Please try again.');
    }
  };

  const helpers: FieldHelpers = { register, errors, watch: (n) => watch(n) };

  // Redesigned split: the sign-up form on the left, the VScanner banner on the right
  // (same banner and scan state as the sign-in page).
  const brandPanel = authBrandPanel();
  const headerEnd = (
    <>
      Already a member?
      <Link to="/auth/login">Login</Link>
    </>
  );

  // Business account → the B2B lead form (captures a DataCentral lead + enforces
  // the free-email block). Individual continues in the simple form below.
  if (step === 'form' && accountType === 'business') {
    return <BusinessLeadRegister onBack={() => setStep('type')} />;
  }

  // Email verification — the individual account is created only after the OTP is
  // confirmed, so fake/unowned emails can't complete signup.
  if (step === 'otp') {
    return (
      <AuthShell
        eyebrow="verify email"
        title="Verify your email"
        subtitle={pendingRef.current?.email}
        footer={FOOTER}
        brandPanel={brandPanel}
        headerEnd={headerEnd}
      >
        <RegisterOtpStep
          email={pendingRef.current?.email ?? ''}
          onVerify={completeRegister}
          onBack={() => {
            setError(null);
            setStep('form');
          }}
        />
      </AuthShell>
    );
  }

  if (step === 'type') {
    return (
      <AuthShell title={`Sign up to ${brand.legalName}`} subtitle={signupSubtitle} footer={FOOTER}
        brandPanel={brandPanel} headerEnd={headerEnd}>
        <div className={styles.typeStack}>
          {error ? (
            <div className={styles.typeError}>
              <Alert tone="danger" title={error} />
            </div>
          ) : null}
          <RadioGroup
            value={accountType}
            onValueChange={(v) => setAccountType(v as AccountType)}
            className={styles.typeList}
          >
            <TypeCard value="business" selected={accountType === 'business'}
              title="Business Account"
              description="Take advantage of our comprehensive solutions and dedicated support to drive business success."
              onSelect={setAccountType} />
            <TypeCard value="individual" selected={accountType === 'individual'}
              title="Individual Account"
              description="For individual needs and personal use."
              onSelect={setAccountType} />
          </RadioGroup>

          <label className={styles.tos}>
            <Checkbox
              checked={agreed}
              onCheckedChange={(v) => {
                // Agreement is only granted by reading the Terms to the end.
                if (v) setTermsOpen(true);
                else setAgreed(false);
              }}
            />
            <span>
              By clicking, you acknowledge and agree to the {COMPANY_NAME}{' '}
              <button type="button" onClick={() => setTermsOpen(true)} className={styles.tosLink}>
                Terms of Service
              </button>
              .
            </span>
          </label>

          <Button
            fullWidth
            size="lg"
            // Gradient when ready; the :disabled rule flattens it to a muted fill
            // (matches the design's ready/disabled states).
            className={`${styles.ctaGradient} ${styles.next}`}
            disabled={!agreed}
            onClick={() => setStep('form')}
          >
            Next
          </Button>

          {brand.features.sso && (
            <>
              <Divider className={form.divider}>Or</Divider>
              <GoogleSignInButton redirectTo={next} onError={setError} captureLead />
            </>
          )}

          <AuthSwitchLink prompt="Already a member?" action="Login" to="/auth/login" state={nextState} />

          <TermsDialog
            open={termsOpen}
            onOpenChange={setTermsOpen}
            onAgree={() => {
              setAgreed(true);
              setTermsOpen(false);
            }}
          />
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell eyebrow="individual" title={`Sign up to ${brand.legalName}`} compact
        footer={FOOTER} brandPanel={brandPanel}>
      <form className={`${form.stack} ${styles.formEnd}`} onSubmit={handleSubmit(onFormSubmit)}>
        <div className={styles.callout}>
          <SsoNotice />
        </div>
        {error ? <Alert tone="danger" title={error} /> : null}
        <IndividualFields {...helpers} />

        <div className={styles.actions}>
          <Button type="button" variant="secondary" size="lg" className={styles.tall} startIcon={<ArrowLeft size={15} />} onClick={() => setStep('type')}>
            Back
          </Button>
          <Button type="submit" size="lg" className={`${styles.ctaGradient} ${styles.grow}`}>
            Continue
          </Button>
        </div>

        <Divider className={form.divider}>Already have account?</Divider>
        <Button type="button" variant="secondary" className={form.secondaryFull} onClick={() => navigate('/auth/login', { state: nextState })}>
          Customer Login
        </Button>
      </form>
    </AuthShell>
  );
}
